import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { GridApi, GridReadyEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ToggleButton } from 'primereact/togglebutton';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useLazySearchSiresQuery } from '@/app/services/lookups';
import { addToast } from '@/app/slices/toast';
import { columnDefs } from '@/components/common/advanced-sire-lookup/column-defs';
import { EditSireDialog } from '@/components/common/edit-sire-dialog';
import { NewSireDialog } from '@/components/common/new-sire-dialog/NewSireDialog';
import { WithLocalStorage } from '@/components/hoc';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, lookupSideBar, sideBar } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Sire } from '@/rtk-query.gql';
import { appendRevisionComment } from '@/utils/formatting';
import { getLocalStorage, setLocalStorage } from '@/utils/storage';

import { useAdvancedSireDataSource } from './useAdvancedSireDataSource';

type IAdvancedSireLookupProps = {
  className?: string;
  label?: string;
  value?: Sire;
  ownerId?: string;
  onChange?: (payload: Sire) => void;
  onClear?: () => void;
  revisionComment?: string;
  setRevisionComment?: (revisionComment: string) => void;
  revisionNumber?: {
    previousRevision: string;
    currentRevision: string;
  };
  setRevisionNumber?: (payload: {
    previousRevision: string;
    currentRevision: string;
  }) => void;
  tooltip?: string;
  tabIndex?: number;
  editable?: boolean;
  disabled?: boolean;
  showAddButton?: boolean;
  showEditButton?: boolean;
};

const DATA_TABLE_ROWS = 50;
const CACHE_BLOCK_SIZE = 500;
const LOAD_DEBOUNCE_MS = 500;

export const AdvancedSireLookup = ({
  className,
  value,
  ownerId,
  label,
  onChange,
  onClear,
  revisionComment,
  setRevisionComment,
  revisionNumber,
  setRevisionNumber,
  tooltip,
  tabIndex,
  editable = false,
  disabled = false,
  showAddButton = true,
  showEditButton = true,
}: IAdvancedSireLookupProps) => {
  const storageKey = 'sire-lookup';
  const { t } = useTranslation();
  const defaultGridOptions = useDefaultGridOptions();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const sireInfiniteScroll = useFeatureFlag('Sire_Infinite_Scroll');
  const persistColumnOrder = useFeatureFlag('Sire_Lookup_Persist_Column_Order');
  const isSearchResetEnabled = useFeatureFlag('DS_SemenSireDonor_Search_Reset');
  const isSpecificViewContextEnabled = useFeatureFlag('Specific_View_Context');
  const ALL_OPTION = { label: t('common.all'), value: '' };
  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );
  const mode = useAppSelector((state) => state.preferences.mode);

  const SEARCH_BY_OPTIONS = columnDefs.map(({ headerName, field }) => ({
    label: headerName,
    value: field,
  }));

  const SEARCH_BY_OPTIONS_WITH_ALL = [ALL_OPTION, ...SEARCH_BY_OPTIONS];

  const gridRef = useRef<AgGridReact>(null);
  const dialogRef = useRef(null);
  const hasRestoredLayoutRef = useRef(false);

  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [searchTermTemp, setSearchTermTemp] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedSearchBy, setSelectedSearchBy] = useState(ALL_OPTION.value);
  const [selectedSire, setSelectedSire] = useState(value);
  const [searchAllSires, setSearchAllSires] = useState(false);
  const [inputValue, setInputValue] = useState<string>(value?.name || '');
  const [newSireDialogVisible, setNewSireDialogVisible] = useState(false);
  const [editSireDialogVisible, setEditSireDialogVisible] = useState(false);
  const [selectedSireForEdit, setSelectedSireForEdit] = useState<
    Sire | undefined
  >();
  const [filterByRanch, setFilterByRanch] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(false);

  const isDonorSchedulingApp = pathname.startsWith('/donor-scheduling');

  useEffect(() => {
    setInputValue(value?.name || '');
    setSelectedSire(value);
  }, [value]);

  useEffect(() => {
    setSelectedSireForEdit(selectedSire);
  }, [selectedSire?.id]);

  const [searchSiresQuery] = useLazySearchSiresQuery();
  const { dataSource } = useAdvancedSireDataSource({
    query: searchSiresQuery,
    searchBy: selectedSearchBy,
    searchTerm,
    ownerId: filterByRanch ? ownerId : undefined,
    gridRef,
    refetchTrigger,
    persistColumnOrder,
  });


  const noRowsOverlayMessage = t('sire.noMatchingSireFound');

  const memoizedDefaultColDef = useMemo(
    () => ({
      ...defaultColDef,
      filter: true,
      filterParams: {
        buttons: ['apply', 'clear'],
        closeOnApply: true,
      },
    }),
    [],
  );

  const saveGridLayout = useCallback((api: GridApi) => {
    setLocalStorage(storageKey, {
      columnState: api.getColumnState(),
      filterModel: api.getFilterModel(),
    });
  }, []);

  const loadGridLayout = useCallback(
    (api: GridApi) => {
      const gridState = getLocalStorage(storageKey);
      if (!gridState) {
        return;
      }

      const savedColumnState = Array.isArray(gridState?.columnState)
        ? gridState.columnState
        : [];

      const currentColumnState = api.getColumnState();
      const savedColumnIds = new Set(
        savedColumnState
          .map((column: { colId?: string }) => column.colId)
          .filter((colId: string | undefined): colId is string => !!colId),
      );
      const newlyAddedColumnIds = currentColumnState
        .map((column) => column.colId)
        .filter(
          (colId): colId is string => !!colId && !savedColumnIds.has(colId),
        );

      api.applyColumnState({
        state: savedColumnState,
        applyOrder: true,
      });
      api.setFilterModel(gridState.filterModel);

      if (newlyAddedColumnIds.length > 0) {
        api.setColumnsVisible(newlyAddedColumnIds, true);
      }
    },
    [],
  );

  const restoreGridLayout = useCallback(
    (api: GridApi) => {
      if (!persistColumnOrder || hasRestoredLayoutRef.current) {
        return;
      }

      hasRestoredLayoutRef.current = true;
      requestAnimationFrame(() => {
        loadGridLayout(api);
      });
    },
    [loadGridLayout, persistColumnOrder],
  );

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      restoreGridLayout(event.api);
    },
    [restoreGridLayout],
  );

  const handleGridStateChanged = useCallback(
    (api: GridApi) => {
      if (persistColumnOrder) {
        saveGridLayout(api);
      }
    },
    [persistColumnOrder, saveGridLayout],
  );

  const closeDialog = useCallback(() => {
    if (isSearchResetEnabled) {
      setSelectedSearchBy(ALL_OPTION.value);
      setSearchTermTemp('');
      setSearchTerm('');
      setFilterByRanch(false);
      gridRef.current?.api?.setFilterModel(null);
    }
    if (gridRef.current?.api) {
      saveGridLayout(gridRef.current.api);
    }
    hasRestoredLayoutRef.current = false;
    setDialogVisible(false);
  }, [ALL_OPTION.value, isSearchResetEnabled, saveGridLayout]);

  const dialogFooterTemplate = (disabled: boolean) => {
    return (
      <div className="flex w-full justify-between pt-4">
        <div>
          <Button
            icon="pi pi-plus"
            label={t('common.new')}
            onClick={() => setNewSireDialogVisible(true)}
            type="button"
          />
          <Button
            icon="pi pi-pencil"
            label={t('common.edit')}
            text
            disabled={selectedSire === undefined}
            onClick={() => {
              if (selectedSire) {
                setSelectedSireForEdit(selectedSire);
              }
              setEditSireDialogVisible(true);
            }}
            type="button"
          />
        </div>
        <div>
          <Button
            label={t('common.cancel')}
            icon="pi pi-times"
            severity="secondary"
            onClick={closeDialog}
            type="button"
          />
          <Button
            label={t('common.select')}
            icon="pi pi-check"
            type="button"
            onClick={() => {
              closeDialog();
              if (selectedSire) {
                if (params.get('revision') === 'true') {
                  const newNoteText = `Change from Sire, ${workingDonorSchedule?.sire?.name} to Sire, ${selectedSire?.name}`;
                  setRevisionComment(
                    appendRevisionComment(revisionComment, newNoteText),
                  );
                  dispatch(
                    addToast({
                      severity: 'info',
                      summary: t('revision.revisionFieldChanged'),
                      detail: t('revision.pleaseUpdateRevisionNumber'),
                      life: ToastLife.MEDIUM,
                    }),
                  );
                }
                setInputValue(selectedSire?.name || '');
                onChange?.(selectedSire);
              }
            }}
            disabled={disabled}
          />
        </div>
      </div>
    );
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement> | undefined) => {
    if (e) {
      e.preventDefault();
    }

    setSearchTerm(searchTermTemp);
  };

  const handleDialogResize = () => {
    if (gridRef.current) {
      gridRef.current.api.sizeColumnsToFit();
    }
  };

  const handleSelectSire = () => {
    if (selectedSire) {
      onChange?.(selectedSire);
      closeDialog();
    }
  };

  const handleSelectionChanged = (e) => {
    const selectedNode = e.api.getSelectedNodes()[0];

    if (selectedNode && selectedNode.data) {
      setSelectedSire(selectedNode.data);
    } else {
      setSelectedSire(undefined);
    }
  };

  const handleOpenDialog = () => {
    setSearchTermTemp(inputValue);
    setSearchTerm(inputValue);
    if (persistColumnOrder) {
      hasRestoredLayoutRef.current = false;
    }
    setDialogVisible(true);
  };

  const handleEditSire = (event: { node: { data: Sire } }) => {
    const selectedNode = event.node;
    if (selectedNode && selectedNode.data) {
      setSelectedSire(selectedNode.data);
      setSelectedSireForEdit(selectedNode.data);
      if (refetchTrigger) {
        setRefetchTrigger(false);
      }
      setEditSireDialogVisible(true);
    }
  };

  const handleSaveSire = () => {
    setEditSireDialogVisible(false);
    setRefetchTrigger(true);
  };

  const persistedGrid = (
    <AgGridReact
      gridOptions={defaultGridOptions}
      className={`ag-theme-transova${mode === 'dark' ? '-dark' : ''} h-full`}
      ref={gridRef}
      defaultColDef={memoizedDefaultColDef}
      columnDefs={columnDefs}
      getRowId={getRowId<Sire>((params) => String(params.data.id))}
      rowModelType="serverSide"
      rowSelection="single"
      cacheBlockSize={CACHE_BLOCK_SIZE}
      blockLoadDebounceMillis={LOAD_DEBOUNCE_MS}
      maintainColumnOrder={true}
      pagination={!sireInfiniteScroll}
      paginationAutoPageSize={!sireInfiniteScroll}
      serverSideDatasource={dataSource ?? undefined}
      paginationPageSize={DATA_TABLE_ROWS}
      onGridReady={handleGridReady}
      onFirstDataRendered={(event) => restoreGridLayout(event.api)}
      onSelectionChanged={handleSelectionChanged}
      onCellDoubleClicked={editable ? handleEditSire : handleSelectSire}
      onColumnMoved={(event) => handleGridStateChanged(event.api)}
      onColumnVisible={(event) => handleGridStateChanged(event.api)}
      onColumnPinned={(event) => handleGridStateChanged(event.api)}
      onFilterChanged={(event) => handleGridStateChanged(event.api)}
      onSortChanged={(event) => handleGridStateChanged(event.api)}
      sideBar={
        isSpecificViewContextEnabled
          ? lookupSideBar('advanced-sire-lookup')
          : sideBar
      }
      overlayNoRowsTemplate={noRowsOverlayMessage}
      overlayLoadingTemplate={t('common.loading')}
    />
  );

  return (
    <div className={`label-input-group col-span-2`}>
      <div className="grid grid-cols-[auto_min-content_min-content] items-end gap-2">
        <div className="label-input-group">
          <label htmlFor="sire-input">{label}</label>
          <InputText
            className={className}
            id="sire-input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleOpenDialog();
              }
            }}
            placeholder={t('sire.searchSire')}
            tabIndex={tabIndex}
            tooltip={tooltip}
            data-skip-enter-advance
            disabled={disabled}
          />
        </div>
        <div className="flex items-end gap-2">
          <Button
            icon="pi pi-search"
            onClick={handleOpenDialog}
            type="button"
            disabled={disabled}
            aria-label={t('sire.searchSire')}
          />
          {showEditButton && (
            <Button
              icon="pi pi-pencil"
              disabled={selectedSire === undefined}
              onClick={() => setEditSireDialogVisible(true)}
              type="button"
              aria-label={t('common.edit')}
            />
          )}
          {showAddButton && (
            <Button
              icon="pi pi-plus"
              onClick={() => setNewSireDialogVisible(true)}
              type="button"
              aria-label={t('common.new')}
            />
          )}
          {ownerId ? (
            <ToggleButton
              offLabel={t('common.all')}
              onLabel={t('common.all')}
              onIcon="pi pi-check"
              offIcon="pi pi-times"
              checked={searchAllSires}
              onChange={(e) => setSearchAllSires(e.value)}
            />
          ) : null}
          {onClear && (
            <Button
              type="button"
              icon="pi pi-times"
              severity="secondary"
              text
              disabled={disabled || !selectedSire}
              tabIndex={-1}
              onClick={onClear}
              aria-label="Clear sire"
            />
          )}
        </div>
      </div>

      <NewSireDialog
        visible={newSireDialogVisible}
        onHide={() => setNewSireDialogVisible(false)}
        onSave={handleSaveSire}
        gridRef={gridRef}
        revisionComment={revisionComment}
        setRevisionComment={setRevisionComment}
        revisionNumber={revisionNumber}
        setRevisionNumber={setRevisionNumber}
      />
      <EditSireDialog
        visible={editSireDialogVisible}
        onHide={() => setEditSireDialogVisible(false)}
        onSave={handleSaveSire}
        sire={selectedSireForEdit}
      />
      <Dialog
        ref={dialogRef}
        className="h-[95vh] w-[75vw]"
        header={t('sire.sireLookup')}
        visible={dialogVisible}
        maximizable
        modal
        onHide={closeDialog}
        footer={() => dialogFooterTemplate(selectedSire === null)}
        onResize={handleDialogResize}
      >
        <div className="flex h-full flex-col">
          <Card className="m-2 w-80">
            <form
              className="flex flex-wrap items-end gap-4"
              onSubmit={handleSearch}
            >
              <div className="label-input-group">
                <label>{t('common.searchBy')}</label>
                <Dropdown
                  value={selectedSearchBy}
                  options={SEARCH_BY_OPTIONS_WITH_ALL}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => {
                    setSelectedSearchBy(e.target.value);
                  }}
                />
              </div>
              <div className="label-input-group">
                <label>{t('common.search')}</label>
                <div className="grid grid-cols-[auto_min-content] items-center gap-2">
                  <InputText
                    value={searchTermTemp}
                    onChange={(e) => setSearchTermTemp(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch(undefined);
                      }
                    }}
                    data-skip-enter-advance
                    disabled={disabled}
                    className="w-57"
                  />
                  <Button
                    icon="pi pi-search"
                    type="button"
                    onClick={() => handleSearch(undefined)}
                    disabled={disabled}
                  />
                </div>
              </div>
              {isDonorSchedulingApp && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={filterByRanch}
                    onChange={(e) => setFilterByRanch(e.checked as boolean)}
                    id="filter-by-ranch"
                  />
                  <label htmlFor="filter-by-ranch">
                    {t('farm.filterByRelatedRanch')}
                  </label>
                </div>
              )}
            </form>
          </Card>
          <div className="m-2 min-h-0 flex-1">
            {persistColumnOrder ? (
              persistedGrid
            ) : (
              <WithLocalStorage storageKey={storageKey}>
                <AgGridReact
                  gridOptions={defaultGridOptions}
                  className={`ag-theme-transova${mode === 'dark' ? '-dark' : ''} h-full`}
                  ref={gridRef}
                  defaultColDef={{
                    ...defaultColDef,
                    filter: true,
                    filterParams: {
                      buttons: ['apply', 'clear'],
                      closeOnApply: true,
                    },
                  }}
                  columnDefs={columnDefs}
                  getRowId={getRowId<Sire>((params) => String(params.data.id))}
                  rowModelType="serverSide"
                  rowSelection="single"
                  cacheBlockSize={CACHE_BLOCK_SIZE}
                  blockLoadDebounceMillis={LOAD_DEBOUNCE_MS}
                  pagination={!sireInfiniteScroll}
                  paginationAutoPageSize={!sireInfiniteScroll}
                  serverSideDatasource={dataSource ?? undefined}
                  paginationPageSize={DATA_TABLE_ROWS}
                  onSelectionChanged={handleSelectionChanged}
                  onCellDoubleClicked={
                    editable ? handleEditSire : handleSelectSire
                  }
                  sideBar={
                    isSpecificViewContextEnabled
                      ? lookupSideBar('advanced-sire-lookup')
                      : sideBar
                  }
                  overlayNoRowsTemplate={noRowsOverlayMessage}
                  overlayLoadingTemplate={t('common.loading')}
                />
              </WithLocalStorage>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
};
